// Standard top-right page affordances (goes in the PageHeader actions slot):
//   - a small, always-visible "Updated <relative>" indicator (exact time on hover)
//   - a "⋮" kebab menu for page-level actions (Export/Import/…) and extra info
//
// Both are optional — render only what a page provides. This is the standard
// home for per-page meta + secondary actions across every list/dashboard.

import type { JSX } from "react";
import { EllipsisVertical, type LucideIcon } from "lucide-react";
import { cn } from "../lib/utils.js";
import { Popover } from "./ui/popover.js";

export interface PageAction {
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
  disabled?: boolean;
}

/** "just now" / "5m ago" / "3h ago" / "2d ago" / else a date. */
export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${String(mins)}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${String(hrs)}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${String(days)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function PageActions({
  updatedAt,
  actions,
}: {
  updatedAt?: string | null;
  actions?: PageAction[];
}): JSX.Element | null {
  const acts = actions ?? [];
  if (!updatedAt && acts.length === 0) return null;
  const exact = updatedAt ? new Date(updatedAt).toLocaleString() : "";

  return (
    <div className="flex items-center gap-1.5">
      {updatedAt && (
        <span className="text-muted-foreground text-xs" title={`As of ${exact}`}>
          Updated {relativeTime(updatedAt)}
        </span>
      )}
      {acts.length > 0 && (
        <Popover
          align="end"
          trigger={({ toggle }) => (
            <button
              type="button"
              onClick={toggle}
              aria-label="Page menu"
              title="More"
              className="text-muted-foreground hover:text-foreground hover:bg-accent rounded-md p-1.5 transition-colors"
            >
              <EllipsisVertical className="size-4" />
            </button>
          )}
        >
          {(close) => (
            <div className="w-52">
              {updatedAt && (
                <div className="text-muted-foreground border-border mb-1 border-b px-2 py-1.5 text-xs">
                  Updated {exact}
                </div>
              )}
              {acts.map((a) => {
                const Icon = a.icon;
                return (
                  <button
                    key={a.label}
                    type="button"
                    disabled={a.disabled}
                    onClick={() => {
                      a.onClick();
                      close();
                    }}
                    className={cn(
                      "hover:bg-accent flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm",
                      "disabled:pointer-events-none disabled:opacity-50",
                    )}
                  >
                    {Icon && <Icon className="size-4" />}
                    {a.label}
                  </button>
                );
              })}
            </div>
          )}
        </Popover>
      )}
    </div>
  );
}
